import { QueryParams } from '@tmf-shared-platform/activated-route/activated-route';
import {
  provideRouterNavigatorService,
  RouterNavigatorService
} from '@tmf-shared-platform/router/router-navigator.service';
import { routerPrepareUrl } from '@tmf-shared-platform/router/router-utils';
import { AsyncDirective, AttributePart, directive, DirectiveResult, PartInfo } from 'lit/async-directive.js';

class RouterLinkDirective extends AsyncDirective {
  private _callback: ((event: Event) => void) | undefined;

  private _path: string | undefined;
  private _queryParams: QueryParams | undefined;
  private _state: Record<string, any> | undefined;

  private _routerNavigatorService: RouterNavigatorService = provideRouterNavigatorService();

  constructor(private _part: PartInfo) {
    super(_part);
  }

  public render(path: string | undefined, queryParams?: QueryParams, state?: Record<string, any>): void {
    if (!path) {
      return;
    }
    if (path?.startsWith('http://') || path?.startsWith('https://')) {
      this._updateHrefRaw(path);
      return;
    }
    this._path = path;
    this._queryParams = queryParams;
    this._state = state;
    if (!this._callback) {
      this._callback = (): void => this._routerNavigatorService.navigateTo(this._path!, this._queryParams, this._state);
      this._host.addEventListener('click', this._callback);
    }
    this._updateHref(path, queryParams);
  }

  public override disconnected(): void {
    this._host.removeEventListener('click', this._callback!);
  }

  private get _host(): HTMLElement {
    return (this._part as AttributePart).element as HTMLElement;
  }

  private _updateHref(path: string, queryParams?: QueryParams): void {
    if ((this._part as AttributePart)?.element?.tagName === 'A') {
      const element: HTMLLinkElement = (this._part as AttributePart).element as HTMLLinkElement;
      element.href = routerPrepareUrl(path, queryParams);
    }
  }

  private _updateHrefRaw(rawUrl: string): void {
    if ((this._part as AttributePart)?.element?.tagName === 'A') {
      const element: HTMLLinkElement = (this._part as AttributePart).element as HTMLLinkElement;
      element.href = rawUrl;
    }
  }
}

type RouterLinkDirectiveResult = (
  path: string | undefined,
  queryParams?: QueryParams,
  state?: Record<string, any>
) => DirectiveResult<typeof RouterLinkDirective>;
export const routerLink: RouterLinkDirectiveResult = directive(RouterLinkDirective);
